/**
 * DadosContagemTab - Aba de Pós-Contagem (Redesenhada)
 *
 * Epic Redesign Pós-Contagem:
 * - Tabela com uma linha por cliente vinculado
 * - Colunas: ID, Cliente, Locais, Lacres, O.C., Início, Término, Envio Rel., Obs., Status
 * - Botão Editar para abrir modal de edição
 * - Importar Excel para preencher dados
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Edit2, Trash2, Download, Info, RefreshCw, FileText, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import { Badge, Tooltip, ActionIcon } from '@mantine/core';
import { MRT_LOCALIZATION_PT_BR } from '../hospital/tableLocalization';
import contagemService, { PosContagemCliente } from '../../services/contagemService';
import { EditPosContagemModal } from './EditPosContagemModal';
import { DeletePosContagemConfirmModal } from './DeletePosContagemConfirmModal';
import { EditableTextCell } from './EditableTextCell';

// Props interface
interface DadosContagemTabProps {
  isNewContagem: boolean;
  openImportInformacoesModal: () => void;
  onDataChange?: () => void;
  refreshKey?: number;  // Epic Redesign Pós-Contagem: Key to trigger refresh from parent
  // Global client filter
  globalClienteFilter?: (string | number)[];
  jobClientes?: { id: number; nome_fantasia?: string; razao_social?: string }[];
  // Guard: lock edição em contagens finalizadas
  jobStatus?: string;
  isMaster?: boolean;
}

// Status badge colors
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  'PENDENTE': { bg: 'gray', text: 'Pendente' },
  'EM_ANDAMENTO': { bg: 'blue', text: 'Em Andamento' },
  'CONCLUIDO': { bg: 'green', text: 'Concluído' },
  // N47: alias legado — o offline antigo gravou 'FINALIZADA' cru (fora das
  // choices) e o badge caía no fallback "Pendente" com a contagem concluída.
  'FINALIZADA': { bg: 'green', text: 'Concluído' },
  'CANCELADO': { bg: 'red', text: 'Cancelado' },
};

export const DadosContagemTab: React.FC<DadosContagemTabProps> = ({
  isNewContagem,
  openImportInformacoesModal,
  onDataChange,
  refreshKey,
  globalClienteFilter,
  jobClientes,
  jobStatus,
  isMaster,
}) => {
  const { id } = useParams<{ id: string }>();
  const jobId = parseInt(id || '0', 10);

  // Guard: bloquear edição em contagens finalizadas (exceto MASTER)
  const TERMINAL_STATUSES = ['EXECUTADA', 'CANCELADA', 'NAO_EXECUTADA'];
  const isJobFinalized = jobStatus && TERMINAL_STATUSES.includes(jobStatus);
  const canModify = !isJobFinalized || !!isMaster;

  const [items, setItems] = useState<PosContagemCliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<PosContagemCliente | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Delete confirmation state
  const [itemToDelete, setItemToDelete] = useState<PosContagemCliente | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter items based on globalClienteFilter
  const filteredItems = useMemo(() => {
    if (!globalClienteFilter || globalClienteFilter.length === 0 || !jobClientes) {
      return items;
    }
    const selectedNames = new Set<string>();
    globalClienteFilter.forEach(id => {
      const cliente = jobClientes.find(c => c.id === Number(id));
      if (cliente) {
        if (cliente.nome_fantasia) selectedNames.add(cliente.nome_fantasia);
        if (cliente.razao_social) selectedNames.add(cliente.razao_social);
      }
    });
    return items.filter(item => item.cliente_nome && selectedNames.has(item.cliente_nome));
  }, [items, globalClienteFilter, jobClientes]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!jobId || isNewContagem) return;
    setLoading(true);
    setError(null);
    try {
      const data = await contagemService.getPosContagemClientes(jobId);
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados');
      console.error('Error fetching pos-contagem:', err);
    } finally {
      setLoading(false);
    }
  }, [jobId, isNewContagem]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleEdit = (item: PosContagemCliente) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleSave = async (data: Partial<PosContagemCliente>) => {
    if (!selectedItem) return;
    if (isJobFinalized && !isMaster) {
      toast.error('Contagem finalizada. Apenas MASTER pode editar.');
      return;
    }
    setIsSaving(true);
    try {
      await contagemService.updatePosContagemCliente(jobId, selectedItem.id, data);
      toast.success('Dados salvos com sucesso!');
      setShowEditModal(false);
      fetchData();
      onDataChange?.();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (item: PosContagemCliente) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    if (isJobFinalized && !isMaster) {
      toast.error('Contagem finalizada. Apenas MASTER pode excluir.');
      return;
    }
    setIsDeleting(true);
    try {
      await contagemService.deletePosContagemCliente(jobId, itemToDelete.id);
      toast.success(`Pós-contagem de "${itemToDelete.cliente_nome}" excluída com sucesso!`);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      fetchData();
      onDataChange?.();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao excluir');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Inline save O.C. with optimistic update
  const handleSaveOC = useCallback(async (itemId: number, newValue: string) => {
    if (isJobFinalized && !isMaster) {
      toast.error('Contagem finalizada. Edição não permitida.');
      return;
    }
    try {
      await contagemService.updatePosContagemCliente(jobId, itemId, { ordem_coleta: newValue });
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, ordem_coleta: newValue } : item
      ));
      toast.success('O.C. atualizada');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao atualizar O.C.');
      throw err; // re-throw so EditableTextCell stays in edit mode
    }
  }, [jobId]);

  // N45: inline save Lacres — mesmo fluxo da O.C.
  const handleSaveLacres = useCallback(async (itemId: number, newValue: string) => {
    if (isJobFinalized && !isMaster) {
      toast.error('Contagem finalizada. Edição não permitida.');
      return;
    }
    try {
      await contagemService.updatePosContagemCliente(jobId, itemId, { lacres: newValue });
      setItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, lacres: newValue } : item
      ));
      toast.success('Lacres atualizados');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao atualizar lacres');
      throw err; // re-throw so EditableTextCell stays in edit mode
    }
  }, [jobId]);

  // Column definitions
  const columns = useMemo<MRT_ColumnDef<PosContagemCliente>[]>(() => [
    {
      accessorKey: 'job_id',
      header: 'ID',
      size: 70,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <span className="font-semibold text-gray-700 dark:text-gray-300">#{row.original.job_id}</span>
      ),
    },
    {
      accessorKey: 'cliente_nome',
      header: 'Cliente',
      size: 180,
      Cell: ({ row }) => (
        <Tooltip label={row.original.cliente_nome} position="top">
          <span className="font-medium text-gray-800 dark:text-gray-200 truncate block max-w-[160px]">
            {row.original.cliente_nome}
          </span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'locais_contados',
      header: 'Locais Contados',
      size: 150,
      Cell: ({ row }) => (
        <Tooltip label={row.original.locais_contados || '-'} position="top">
          <span className="text-gray-600 dark:text-gray-400 truncate block max-w-[130px]">
            {row.original.locais_contados || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'lacres',
      header: 'Lacres',
      size: 120,
      Cell: ({ row }) => (
        <EditableTextCell
          value={row.original.lacres}
          onSave={(val) => handleSaveLacres(row.original.id, val)}
          placeholder="Ex: 0977552"
        />
      ),
    },
    {
      accessorKey: 'ordem_coleta',
      header: 'O.C.',
      size: 150,
      Cell: ({ row }) => (
        <EditableTextCell
          value={row.original.ordem_coleta}
          onSave={(val) => handleSaveOC(row.original.id, val)}
          placeholder="Ex: OC-534-001"
        />
      ),
    },
    {
      accessorKey: 'data_hora_inicio',
      header: 'Início',
      size: 130,
      Cell: ({ row }) => (
        <span className="text-gray-600 dark:text-gray-400 text-xs">{row.original.data_hora_inicio || '-'}</span>
      ),
    },
    {
      accessorKey: 'data_hora_termino',
      header: 'Término',
      size: 130,
      Cell: ({ row }) => (
        <span className="text-gray-600 dark:text-gray-400 text-xs">{row.original.data_hora_termino || '-'}</span>
      ),
    },
    {
      accessorKey: 'data_envio_relatorio',
      header: 'Envio Rel.',
      size: 130,
      Cell: ({ row }) => (
        <span className="text-gray-600 dark:text-gray-400 text-xs">{row.original.data_envio_relatorio || '-'}</span>
      ),
    },
    {
      accessorKey: 'observacao',
      header: 'Observação',
      size: 150,
      Cell: ({ row }) => (
        <Tooltip label={row.original.observacao || '-'} position="top">
          <span className="text-gray-600 dark:text-gray-400 truncate block max-w-[130px]">
            {row.original.observacao || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => {
        const status = row.original.status || 'PENDENTE';
        const { bg, text } = STATUS_COLORS[status] || STATUS_COLORS['PENDENTE'];
        return <Badge color={bg} variant="filled" size="sm">{text}</Badge>;
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      size: 100,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canModify ? (
            <>
              <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(row.original)} title="Editar">
                <Edit2 size={16} />
              </ActionIcon>
              <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteClick(row.original)} title="Excluir">
                <Trash2 size={16} />
              </ActionIcon>
            </>
          ) : (
            <span className="text-xs text-gray-400">Bloqueado</span>
          )}
        </div>
      ),
    },
  ], [handleSaveOC, handleSaveLacres]);

  // Table instance
  const table = useMantineReactTable({
    columns,
    data: filteredItems,
    enableColumnActions: false,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableGlobalFilter: true,
    initialState: {
      density: 'xs',
      pagination: { pageIndex: 0, pageSize: 10 },
    },
    localization: MRT_LOCALIZATION_PT_BR,
    mantineTableContainerProps: { style: { maxHeight: 'calc(100vh - 400px)' } },
    mantineTableProps: {
      striped: true,
      highlightOnHover: true,
      withColumnBorders: true,
      style: { fontSize: '13px' },
    },
    mantinePaperProps: { shadow: 'none', style: { border: 'none' } },
    renderTopToolbarCustomActions: () => (
      <div className="flex items-center gap-2">
        <button
          onClick={() => fetchData()}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
        <button
          onClick={openImportInformacoesModal}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition"
        >
          <Download size={14} className="rotate-180" />
          Importar Excel
        </button>
      </div>
    ),
  });

  if (isNewContagem) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Salve a contagem primeiro para acessar a aba Pós-Contagem.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header with Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <FileText size={20} className="text-emerald-600" />
            Pós-Contagem
          </h2>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <Info size={16} />
          </button>
        </div>
        {filteredItems.length > 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredItems.length} cliente{filteredItems.length !== 1 ? 's' : ''}
            {globalClienteFilter && globalClienteFilter.length > 0 && items.length !== filteredItems.length && (
              <span className="ml-1 text-blue-500">(de {items.length} total)</span>
            )}
          </span>
        )}
      </div>

      {/* Info Box */}
      {showInfo && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-100 space-y-1">
            <p><strong>Pós-Contagem por Cliente:</strong></p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Cada cliente vinculado à contagem tem sua própria linha</li>
              <li>Clique no botão Editar para modificar os dados de cada cliente</li>
              <li>Use Importar Excel para preencher dados de múltiplos clientes</li>
              <li>O arquivo Excel deve conter: Cliente (CNPJ ou Nome), Locais, Lacres, O.C., Início, Término, Envio Rel., Obs., Status</li>
            </ul>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && items.length === 0 && (
        <div className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" />
          <p className="mt-2 text-gray-500 dark:text-gray-400">Carregando...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && !error && (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <FileText size={40} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p>Nenhum cliente vinculado a esta contagem.</p>
          <p className="text-sm mt-1">Vincule clientes na aba "Dados Gerais" primeiro.</p>
        </div>
      )}

      {filteredItems.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <MantineReactTable table={table} />
        </div>
      )}

      {/* Edit Modal */}
      <EditPosContagemModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedItem(null); }}
        onSave={handleSave}
        item={selectedItem}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Modal */}
      <DeletePosContagemConfirmModal
        isOpen={showDeleteConfirm}
        item={itemToDelete}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

// Interface for old implementation compatibility - deprecated
export interface PosContagemData {
  id: number;
  clientes: { id: number; nome_fantasia?: string; razao_social?: string }[];
  status: string;
  ordem_coleta: string | null;
  lacres_pos_contagem: string | null;
  data_hora_inicio_contagem: string | null;
  data_hora_termino_contagem: string | null;
  data_envio_relatorio: string | null;
  locais_contados: string | null;
  observacao_pos_contagem: string | null;
}
